import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { getOrderSession, xsipOrder, getSchemes, getMandateStatus } from "../../../api/bseApi";

const FREQUENCY_TYPES = [
  { value: "MONTHLY", label: "MONTHLY" },
  { value: "WEEKLY", label: "WEEKLY" },
];
const FIRST_ORDER_FLAGS = [
  { value: "Y", label: "Yes (Y)" },
  { value: "N", label: "No (N)" },
];
const EUIN_DECLARATION = [
  { value: "Y", label: "Yes (Y)" },
  { value: "N", label: "No (N)" },
];

function formatDateForBse(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

function isFutureDate(dateStr) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return d > today;
}
  

/**
 * XsipIsipRegistrationScreen - BSE StAR MF XSIP/ISIP Order Entry
 * Opens only if ClientCode and MandateID exist
 * Receives: ClientCode, MandateID (XSIPMANDATEID), EUIN, Scheme master list
 */
export default function XsipIsipRegistrationScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams();
  const params = new URLSearchParams(location.search);
  const [context, setContext] = useState(null);
  const [schemes, setSchemes] = useState([]);
  const [mandates, setMandates] = useState([]);
  const [mandateStep, setMandateStep] = useState(false);
  const [form, setForm] = useState({
    schemeCd: "",
    schemeCdManual: "",
    startDate: "",
    frequencyType: "MONTHLY",
    installmentAmount: "",
    noOfInstallments: "",
    firstOrderFlag: "N",
    euin: "",
    euinDeclarationFlag: "N",
  });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const state = location.state;
    const clientCode = state?.clientCode || params.get("clientCode");
    const mandateId = state?.mandateId || params.get("mandateId") || params.get("xsipMandateId");
    const euin = state?.euin || params.get("euin") || "";

    if (clientCode && mandateId) {
      setContext({
        clientCode,
        mandateId,
        euin: euin || "",
      });
      setForm((f) => ({ ...f, euin: euin || f.euin }));
      setFetching(false);
      return;
    }
    if (clientCode && !mandateId) {
      setContext({ clientCode, euin: euin || "" });
      setForm((f) => ({ ...f, euin: euin || f.euin }));
      setMandateStep(true);
      setFetching(false);
      return;
    }

    if (userId) {
      const fetchUser = async () => {
        try {
          const res = await axios.get(
            `${process.env.REACT_APP_BACKEND_BASE_URL || "http://localhost:8000/api"}/user/${userId}`
          );
          const user = res.data?.status === 1 ? res.data?.data : null;
          if (!user) {
            Swal.fire("Error", "User not found.", "error").then(() => navigate("/user"));
            return;
          }
          const ucc = user.ucc ?? user.UCC ?? "";
          const uccValid = ucc && String(ucc).trim() !== "" && String(ucc) !== "Reject";
          if (!uccValid) {
            Swal.fire("Error", "ClientCode (UCC) not available. Complete UCC creation first.", "error").then(() => navigate("/user"));
            return;
          }
          const mandateIdFromState = state?.mandateId || params.get("mandateId") || params.get("xsipMandateId");
          if (mandateIdFromState) {
            setContext({
              clientCode: String(ucc).trim(),
              mandateId: mandateIdFromState,
              euin: euin || "",
            });
            setForm((f) => ({ ...f, euin: euin || f.euin }));
          } else {
            setContext({ clientCode: String(ucc).trim(), euin: euin || "" });
            setMandateStep(true);
          }
        } catch (err) {
          Swal.fire("Error", err.response?.data?.error || "Failed to load user.", "error").then(() => navigate("/user"));
        } finally {
          setFetching(false);
        }
      };
      fetchUser();
      return;
    }

    Swal.fire("Error", "Missing ClientCode. Open from User Listing.", "error").then(() => navigate("/user"));
    setFetching(false);
  }, [location.state, location.search, userId, navigate]);

  useEffect(() => {
    if (!context?.clientCode || context?.mandateId) return;
    const loadMandates = async () => {
      try {
        const fromDate = "01/01/2020";
        const toDate = new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }).replace(/\//g, "/");
        const res = await getMandateStatus({
          fromDate,
          toDate,
          clientCode: context.clientCode,
        });
        if (res.data?.status === 1 && Array.isArray(res.data?.data)) {
          setMandates(res.data.data);
        } else if (res.data?.mandateDetails && Array.isArray(res.data.mandateDetails)) {
          setMandates(res.data.mandateDetails);
        }
      } catch (err) {
        console.warn("Mandate list load failed:", err.message);
      }
    };
    loadMandates();
  }, [context?.clientCode, context?.mandateId]);

  useEffect(() => {
    const loadSchemes = async () => {
      try {
        const res = await getSchemes();
        if (res.data?.status === 1 && Array.isArray(res.data?.data)) {
          setSchemes(res.data.data);
        }
      } catch (err) {
        console.warn("Scheme list load failed:", err.message);
      }
    };
    loadSchemes();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const validate = () => {
    const err = {};
    const schemeCd = (form.schemeCd || form.schemeCdManual || "").trim();
    if (!schemeCd) err.schemeCd = "Scheme selection is mandatory";
    if (!form.startDate) err.startDate = "Start Date is mandatory";
    else if (!isFutureDate(form.startDate)) err.startDate = "Start Date must be a future date";
    const amt = parseFloat(form.installmentAmount);
    if (!form.installmentAmount || isNaN(amt) || amt <= 0) err.installmentAmount = "Installment Amount must be greater than 0";
    const n = parseInt(form.noOfInstallments, 10);
    if (!form.noOfInstallments || isNaN(n) || n <= 0) err.noOfInstallments = "Number of Installments must be greater than 0";
    const euinTrim = (form.euin || "").trim();
    if (!euinTrim) err.euin = "EUIN is mandatory";
    if (!context?.mandateId) err.mandateId = "MandateID must exist";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!context || !validate()) return;

    setLoading(true);
    try {
      const sessionRes = await getOrderSession();
      if (sessionRes.data?.status !== 1) {
        const msg = sessionRes.data?.error || sessionRes.data?.message || "Session failed";
        Swal.fire("Error", msg, "error");
        setLoading(false);
        return;
      }

      const { encryptedPassword, passKey } = sessionRes.data.data;
      const schemeCd = (form.schemeCd || form.schemeCdManual || "").trim();
      const startDateFormatted = formatDateForBse(form.startDate);

      const payload = {
        encryptedPassword,
        passKey,
        clientCode: context.clientCode,
        schemeCd,
        xsipMandateId: context.mandateId,
        startDate: startDateFormatted,
        frequencyType: form.frequencyType,
        frequencyAllowed: 1,
        installmentAmount: parseFloat(form.installmentAmount),
        noOfInstallments: parseInt(form.noOfInstallments, 10),
        firstOrderFlag: form.firstOrderFlag,
        euin: form.euin.trim(),
        euinDeclarationFlag: form.euinDeclarationFlag,
        transMode: "D",
        dpTransactionMode: "P",
        dpc: "Y",
      };

      const res = await xsipOrder(payload);

      if (res.data?.status === 1) {
        const xsipRegId = res.data?.data?.xsipRegId;
        Swal.fire({
          icon: "success",
          title: "XSIP/ISIP Registered",
          text: `XSIP/ISIP registered successfully. Reg ID: ${xsipRegId || "N/A"}`,
        });
        navigate("/user");
      } else {
        const msg = res.data?.bseMessage || res.data?.message || res.data?.error || "XSIP/ISIP registration failed";
        Swal.fire("Error", msg, "error");
      }
    } catch (err) {
      const msg =
        err.response?.data?.bseMessage ||
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "XSIP/ISIP registration failed";
      Swal.fire("Error", msg, "error");
    } finally {
      setLoading(false);
    }
  };

  const onMandateSelect = (mandateId) => {
    setContext((c) => ({ ...c, mandateId }));
    setMandateStep(false);
  };

  if (fetching || !context) {
    return (
      <div className="main-content">
        <div className="page-wrapper page-settings">
          <div className="content p-4">
            <div className="text-center">
              <span className="spinner-border spinner-border-sm me-2" role="status" />
              Loading...
            </div>
          </div>
        </div>
      </div>
    );
  }

  const useManualScheme = schemes.length === 0;

  if (mandateStep && !context.mandateId) {
    const mandateList = mandates || [];
    return (
      <div className="main-content">
        <div className="page-wrapper page-settings">
          <div className="content">
            <div className="mb-4" style={{ background: "#ffffff", borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)", padding: "20px" }}>
              <nav aria-label="breadcrumb"><ol className="breadcrumb">
                <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link></li>
                <li className="breadcrumb-item"><Link to="/user">Users</Link></li>
                <li className="breadcrumb-item active">XSIP/ISIP - Select Mandate</li>
              </ol></nav>
              <h5>Select XSIP Mandate</h5>
              <p className="text-muted mb-0">Client Code: {context.clientCode}</p>
            </div>
            <div style={{ background: "#ffffff", borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)", padding: "24px" }}>
              {mandateList.length === 0 ? (
                <p className="text-muted">No mandates found. Register a mandate (XSIP/e-NACH/OTM) first.</p>
              ) : (
                <div className="list-group">
                  {mandateList.map((m, i) => (
                    <button
                      key={i}
                      type="button"
                      className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                      onClick={() => onMandateSelect(m.MandateId || m.mandateId || m.MandateID || m.id || String(i + 1))}
                    >
                      <span>Mandate ID: {m.MandateId || m.mandateId || m.MandateID || m.id || i + 1}</span>
                      <i className="fas fa-chevron-right" />
                    </button>
                  ))}
                </div>
              )}
              <div className="mt-3">
                <Link to="/user" className="btn btn-secondary">Cancel</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="page-wrapper page-settings">
        <div className="content">
          <div
            className="mb-4"
            style={{
              background: "#ffffff",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              padding: "20px",
            }}
          >
            <nav aria-label="breadcrumb" style={{ marginBottom: "15px" }}>
              <ol className="breadcrumb">
                <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link></li>
                <li className="breadcrumb-item"><Link to="/user">Users</Link></li>
                <li className="breadcrumb-item active">XSIP/ISIP Registration</li>
              </ol>
            </nav>
            <h5>XSIP / ISIP Registration</h5>
            <p className="text-muted mb-0">
              Client Code: {context.clientCode} | Mandate ID: {context.mandateId}
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div
              className="mb-4"
              style={{
                background: "#ffffff",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                padding: "24px",
                border: "1px solid #e6eef0",
              }}
            >
              <h6 className="mb-4" style={{ color: "#0b6f68", fontWeight: "600" }}>
                SIP Details (Mandate Based)
              </h6>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Scheme (SchemeCd) <span className="text-danger">*</span></label>
                  {useManualScheme ? (
                    <input
                      type="text"
                      className={`form-control ${errors.schemeCd ? "is-invalid" : ""}`}
                      name="schemeCdManual"
                      value={form.schemeCdManual}
                      onChange={handleChange}
                      placeholder="Enter scheme code"
                    />
                  ) : (
                    <select
                      className={`form-select ${errors.schemeCd ? "is-invalid" : ""}`}
                      name="schemeCd"
                      value={form.schemeCd}
                      onChange={handleChange}
                    >
                      <option value="">Select Scheme</option>
                      {schemes.map((s) => (
                        <option key={s.schemeCd} value={s.schemeCd}>
                          {s.schemeName || s.schemes || s.schemeCd} ({s.schemeCd})
                        </option>
                      ))}
                    </select>
                  )}
                  {errors.schemeCd && <div className="invalid-feedback">{errors.schemeCd}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Start Date (Future) <span className="text-danger">*</span></label>
                  <input
                    type="date"
                    className={`form-control ${errors.startDate ? "is-invalid" : ""}`}
                    name="startDate"
                    value={form.startDate}
                    onChange={handleChange}
                  />
                  {errors.startDate && <div className="invalid-feedback">{errors.startDate}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Frequency (FREQUENCYTYPE)</label>
                  <select
                    className="form-select"
                    name="frequencyType"
                    value={form.frequencyType}
                    onChange={handleChange}
                  >
                    {FREQUENCY_TYPES.map((f) => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Mandate (XSIPMANDATEID)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={context.mandateId}
                    readOnly
                    style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Installment Amount (INR) <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    className={`form-control ${errors.installmentAmount ? "is-invalid" : ""}`}
                    name="installmentAmount"
                    value={form.installmentAmount}
                    onChange={handleChange}
                    min="1"
                    step="0.01"
                    placeholder="e.g. 5000"
                  />
                  {errors.installmentAmount && <div className="invalid-feedback">{errors.installmentAmount}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Number of Installments <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    className={`form-control ${errors.noOfInstallments ? "is-invalid" : ""}`}
                    name="noOfInstallments"
                    value={form.noOfInstallments}
                    onChange={handleChange}
                    min="1"
                    placeholder="e.g. 12"
                  />
                  {errors.noOfInstallments && <div className="invalid-feedback">{errors.noOfInstallments}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">First Order Today (FIRSTORDERFLAG)</label>
                  <select
                    className="form-select"
                    name="firstOrderFlag"
                    value={form.firstOrderFlag}
                    onChange={handleChange}
                  >
                    {FIRST_ORDER_FLAGS.map((f) => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label">EUIN <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className={`form-control ${errors.euin ? "is-invalid" : ""}`}
                    name="euin"
                    value={form.euin}
                    onChange={handleChange}
                    placeholder="e.g. E12345678901234"
                  />
                  {errors.euin && <div className="invalid-feedback">{errors.euin}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">EUIN Declaration (EUINDECLARATIONFLAG)</label>
                  <select
                    className="form-select"
                    name="euinDeclarationFlag"
                    value={form.euinDeclarationFlag}
                    onChange={handleChange}
                  >
                    {EUIN_DECLARATION.map((f) => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="d-flex gap-2 justify-content-end mt-4 pt-3" style={{ borderTop: "1px solid #e6eef0" }}>
                <Link to="/user" className="btn btn-secondary">
                  Cancel
                </Link>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" />
                      Registering...
                    </>
                  ) : (
                    "Register XSIP/ISIP"
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
